import type MainProject from '@/model/MainProject'
import DataFileMap from '@/model/Resource/DataFileMap'
import WelfareModel from '@/model/Welfare/WelfareModel'
import { FileWriter } from '@/platform'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import { createProjectFontObjectUrl } from '@/features/project-font/runtime'
import { writeMallNewBin } from '@/features/project-mall/runtime'
import { buildPublishArtifacts } from '@/publish/services/buildPublishArtifacts'
import { applySafariAudioRewriteForTest } from '@/features/audio-playback/safariAudioPlayback'
import { resolveEnginePathFromRelativePath } from '@/features/virtual-resource/rules'
import type { ResourceCategoryKey } from '@/features/virtual-resource/constants'
import type { PublishArtifacts, PublishResourceRoots } from '@/publish/types'

export interface PreviewTestBootInfo {
  testFlowers: number
  canvasWidth: number
  canvasHeight: number
  clientType: number
  binSource: 'memory'
  MapID: number
  /** 工程 GUID，引擎启动写入全局 `guid`（`info.aGuid`），供存读档 XOR 密钥使用 */
  aGuid: string
  saveStorageKey: string
  memoryBins: Record<string, Uint8Array>
}

export interface PreviewTestSession {
  id: number
  bootInfo: PreviewTestBootInfo
  requestRewriteMap: Record<string, string>
  cleanupObjectUrls: string[]
}

function logPreviewTest(_message: string, _payload?: string | Record<string, unknown>): void {}

type ResourceLeafMeta = {
  name: string
  relativePath: string
  mappingPath: string
  resourceUrl: string
}

function normalizePath(value: string): string {
  return String(value || '')
    .replace(/\\/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+/g, '/')
    .trim()
}

function normalizeRuntimePath(value: string): string {
  return normalizePath(value).toLowerCase()
}

function md5Text(value: string): string {
  return BrowserFileUtils.getMd5HashFromString(value)
}

function joinSegments(...segments: Array<string | null | undefined>): string {
  return segments
    .map((item) => normalizePath(item ?? ''))
    .filter(Boolean)
    .join('/')
}

function collectResourceLeaves(root: DataFileMap | null | undefined): ResourceLeafMeta[] {
  if (!root) return []
  const out: ResourceLeafMeta[] = []
  const stack: DataFileMap[] = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    out.push({
      name: node.name,
      relativePath: node.relativePath,
      mappingPath: node.mappingPath,
      resourceUrl: node.resourceUrl,
    })
  }
  return out
}

function resolveStoredPath(meta: ResourceLeafMeta, fallbackMappingPath: string): string {
  const mappingPath = normalizePath(meta.mappingPath || fallbackMappingPath)
  const filePath = normalizePath(meta.relativePath || meta.name)
  return joinSegments(mappingPath, filePath) || filePath
}

function tryExtractMd5FromUrl(resourceUrl: string): string {
  try {
    const url = new URL(resourceUrl, window.location.href)
    const matches = url.pathname.match(/[a-f0-9]{32}/gi) ?? []
    const last = matches[matches.length - 1] ?? ''
    return /^[a-f0-9]{32}$/i.test(last) ? last.toLowerCase() : ''
  } catch {
    return ''
  }
}

function cloneResourceRoot(root: DataFileMap | null): DataFileMap | null {
  if (!root) return null
  return new DataFileMap(root.toJson())
}

function patchFontRootForPreviewTest(
  root: DataFileMap | null,
  projectFontObjectUrl: {
    storedPath: string
    fileName: string
    objectUrl: string
  } | null,
): DataFileMap | null {
  if (!root || !projectFontObjectUrl?.objectUrl) return root

  const patchedRoot = cloneResourceRoot(root)
  if (!patchedRoot) return patchedRoot

  const targetNames = new Set([
    normalizeRuntimePath(projectFontObjectUrl.storedPath),
    normalizeRuntimePath(projectFontObjectUrl.fileName),
  ])

  const stack: DataFileMap[] = [patchedRoot]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      for (let index = node.children.length - 1; index >= 0; index -= 1) {
        stack.push(node.children[index]!)
      }
      continue
    }
    const candidates = [
      normalizeRuntimePath(node.relativePath),
      normalizeRuntimePath(node.name),
      normalizeRuntimePath(resolveStoredPath({
        name: node.name,
        relativePath: node.relativePath,
        mappingPath: node.mappingPath,
        resourceUrl: node.resourceUrl,
      }, 'Font')),
    ]
    if (!candidates.some((item) => item && targetNames.has(item))) continue
    node.resourceUrl = projectFontObjectUrl.objectUrl
  }

  return patchedRoot
}

function buildRequestRewriteMap(params: {
  resourceRoots: PublishResourceRoots
  previewOverrides?: Record<string, string>
  artifacts: PublishArtifacts
}): { map: Record<string, string>; cleanupObjectUrls: string[]; audioRewriteMd5Keys: string[] } {
  const out: Record<string, string> = {}
  const cleanupObjectUrls: string[] = []
  const audioRewriteMd5Keys: string[] = []
  const manifestMd5ByPath = new Map(
    params.artifacts.mapEntries.map((entry) => [
      normalizeRuntimePath(entry.relativePath),
      entry.md5.toLowerCase(),
    ]),
  )
  const groups: Array<{ root: DataFileMap | null; fallback: string; category: ResourceCategoryKey }> = [
    { root: params.resourceRoots.graphic, fallback: 'Graphics', category: 'graphic' },
    { root: params.resourceRoots.audio, fallback: 'Audio', category: 'audio' },
    { root: params.resourceRoots.video, fallback: 'Graphics/video', category: 'video' },
  ]

  for (const group of groups) {
    for (const leaf of collectResourceLeaves(group.root)) {
      const actualUrl = params.previewOverrides?.[leaf.resourceUrl] ?? leaf.resourceUrl
      if (!actualUrl || actualUrl.startsWith('cgbitmap://')) continue
      const relativePath = resolveStoredPath(leaf, group.fallback)
      const candidates = new Set<string>()
      const addMd5Candidate = (value: string): void => {
        if (value) candidates.add(value)
      }
      const addPathMd5Candidate = (value: string): void => {
        if (normalizePath(value)) candidates.add(md5Text(value))
      }
      const leafRelativePath = leaf.relativePath || leaf.name
      addMd5Candidate(manifestMd5ByPath.get(normalizeRuntimePath(relativePath)) ?? '')
      addMd5Candidate(tryExtractMd5FromUrl(leaf.resourceUrl))
      addMd5Candidate(tryExtractMd5FromUrl(actualUrl))
      addPathMd5Candidate(relativePath)
      addPathMd5Candidate(resolveEnginePathFromRelativePath(group.category, leaf.mappingPath, leafRelativePath))
      addPathMd5Candidate(leafRelativePath)
      addPathMd5Candidate(leaf.name)
      for (const md5 of candidates) {
        if (!md5) continue
        const md5Key = md5.toLowerCase()
        out[md5Key] = actualUrl
        if (group.category === 'audio') {
          audioRewriteMd5Keys.push(md5Key)
        }
      }
    }
  }

  for (const file of params.artifacts.fontFiles) {
    const buffer = file.bytes.buffer.slice(file.bytes.byteOffset, file.bytes.byteOffset + file.bytes.byteLength) as ArrayBuffer
    const objectUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/octet-stream' }))
    out[file.md5.toLowerCase()] = objectUrl
    cleanupObjectUrls.push(objectUrl)
  }

  return {
    map: out,
    cleanupObjectUrls,
    audioRewriteMd5Keys,
  }
}

function writePreviewWelfareBin(project: MainProject): Uint8Array {
  const writer = new FileWriter()
  const welfare = project.Welfare ?? new WelfareModel({
    Width: project.screenHash.width,
    Height: project.screenHash.height,
  })
  welfare.writeVersionedOui(writer)
  return writer.toUint8Array()
}

/**
 * 读取工程 GUID；测试启动与存档命名空间必须用同一份，空则无法启动。
 */
function getPreviewProjectGuid(project: MainProject): string {
  const guid = project.projectHeader.ID?.trim()
  if (!guid) {
    throw new Error('工程 GUID 为空，无法启动测试')
  }
  return guid
}

/**
 * 构建预览测试会话：生成与发布共用的产物，组装 memoryBins 与 MD5 重写表。
 *
 * 执行顺序：
 * 1. 补丁 TTF 字体根供引擎加载
 * 2. `buildPublishArtifacts`（avoid-remote）→ game.bin / map.bin / 位图字体清单
 * 3. 补充 mallnew.bin、welfare.bin
 * 4. `buildRequestRewriteMap`：资源 MD5 → blob URL；Safari 音频 MIME 修正
 *
 * 前置条件：调用方已执行 `generateWorkspaceBitmapFont()`。
 *
 * @param params.project 当前唯一 MainProject
 * @param params.resourceRoots 虚拟资源四分类根
 * @param params.selectedMapId 测试起始剧情 Id
 * @param params.previewOverrides 预览临时 URL 覆盖
 */
export async function buildPreviewTestSession(params: {
  project: MainProject
  resourceRoots: PublishResourceRoots
  selectedMapId?: number | null
  previewOverrides?: Record<string, string>
}): Promise<PreviewTestSession> {
  const projectGuid = getPreviewProjectGuid(params.project)
  const projectFontObjectUrl = await createProjectFontObjectUrl(params.project)
  logPreviewTest('session:start', {
    projectId: projectGuid,
    selectedMapId: params.selectedMapId ?? 0,
    hasProjectFontObjectUrl: !!projectFontObjectUrl?.objectUrl,
  })

  const patchedResourceRoots: PublishResourceRoots = {
    ...params.resourceRoots,
    font: patchFontRootForPreviewTest(params.resourceRoots.font, projectFontObjectUrl),
  }

  const artifacts = await buildPublishArtifacts(params.project, patchedResourceRoots, {
    networkPolicy: 'avoid-remote',
    skipWordCount: true,
    onLog: (message, payload) => logPreviewTest(`artifacts:${message}`, payload),
  })
  const memoryBins: Record<string, Uint8Array> = {
    'map.bin': artifacts.mapBin.bytes,
  }

  for (const file of artifacts.runtimeFiles) {
    const key = normalizeRuntimePath(file.relativePath)
    if (!key) continue
    memoryBins[key] = file.bytes
  }

  if (!memoryBins['data/mallnew.bin']) {
    memoryBins['data/mallnew.bin'] = writeMallNewBin(params.project)
  }
  if (!memoryBins['data/welfare.bin']) {
    memoryBins['data/welfare.bin'] = writePreviewWelfareBin(params.project)
  }

  logPreviewTest('session:memory-bins', Object.keys(memoryBins).join('\n'))
  logPreviewTest('session:map-bin-mapping')

  const requestRewrite = buildRequestRewriteMap({
    resourceRoots: patchedResourceRoots,
    previewOverrides: params.previewOverrides,
    artifacts,
  })
  const safariAudioRewrite = await applySafariAudioRewriteForTest(
    requestRewrite.map,
    requestRewrite.audioRewriteMd5Keys,
  )
  logPreviewTest('session:request-rewrite-map', {
    count: Object.keys(safariAudioRewrite.map).length,
  })

  return {
    id: Date.now() + Math.floor(Math.random() * 1000),
    bootInfo: {
      testFlowers: 1000,
      canvasWidth: params.project.screenHash.width,
      canvasHeight: params.project.screenHash.height,
      clientType: 0,
      binSource: 'memory',
      MapID: Math.max(0, Number(params.selectedMapId ?? 0) || 0),
      aGuid: projectGuid,
      saveStorageKey: joinSegments(projectGuid, 'current'),
      memoryBins,
    },
    requestRewriteMap: safariAudioRewrite.map,
    cleanupObjectUrls: [
      ...requestRewrite.cleanupObjectUrls,
      ...safariAudioRewrite.cleanupObjectUrls,
      ...(projectFontObjectUrl?.objectUrl ? [projectFontObjectUrl.objectUrl] : []),
    ],
  }
}
